// ────────────────────────────────────────────────────────────────────────────
// Localisation foundation for @my/tms.
//
// Single-source-of-truth init for `react-i18next` (the underlying library —
// the folder is named after the broader concept, "localisation", which also
// covers number / date / currency formatting and pluralisation as those land).
// Bundled English resources; other languages get added by dropping a sibling
// JSON under `locales/<code>/` and listing it in `resources` below.
//
// The runtime language is *not* auto-detected from the browser — it is driven
// by the loaded organisation's `languageCode` (see `SyncLanguageWithOrg`).
// English is the boot default and the unconditional fallback for any code we
// don't yet ship translations for.
//
// New strings live in `locales/en/<namespace>.json`. Namespaces split by major
// feature area (one JSON per folder under `apps/tms/src/features/<x>`) so the
// blast radius of adding a new language is contained per-feature. Add the new
// namespace to `resources` below and to the typed-keys augmentation in
// `types.d.ts` — the rest is automatic.
// ────────────────────────────────────────────────────────────────────────────
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import enCommon from './locales/en/common.json';
import enConfiguration from './locales/en/configuration.json';
import enDashboard from './locales/en/dashboard.json';
import enLocations from './locales/en/locations.json';
import enNavigation from './locales/en/navigation.json';
import enOrganisation from './locales/en/organisation.json';
import enParties from './locales/en/parties.json';
import enResources from './locales/en/resources.json';
import esCommon from './locales/es/common.json';
import esConfiguration from './locales/es/configuration.json';
import esDashboard from './locales/es/dashboard.json';
import esLocations from './locales/es/locations.json';
import esNavigation from './locales/es/navigation.json';
import esOrganisation from './locales/es/organisation.json';
import esParties from './locales/es/parties.json';
import esResources from './locales/es/resources.json';
import frCommon from './locales/fr/common.json';
import frConfiguration from './locales/fr/configuration.json';
import frDashboard from './locales/fr/dashboard.json';
import frLocations from './locales/fr/locations.json';
import frNavigation from './locales/fr/navigation.json';
import frOrganisation from './locales/fr/organisation.json';
import frParties from './locales/fr/parties.json';
import frResources from './locales/fr/resources.json';
import deCommon from './locales/de/common.json';
import deConfiguration from './locales/de/configuration.json';
import deDashboard from './locales/de/dashboard.json';
import deLocations from './locales/de/locations.json';
import deNavigation from './locales/de/navigation.json';
import deOrganisation from './locales/de/organisation.json';
import deParties from './locales/de/parties.json';
import deResources from './locales/de/resources.json';
import itCommon from './locales/it/common.json';
import itConfiguration from './locales/it/configuration.json';
import itDashboard from './locales/it/dashboard.json';
import itLocations from './locales/it/locations.json';
import itNavigation from './locales/it/navigation.json';
import itOrganisation from './locales/it/organisation.json';
import itParties from './locales/it/parties.json';
import itResources from './locales/it/resources.json';
import ptCommon from './locales/pt/common.json';
import ptConfiguration from './locales/pt/configuration.json';
import ptDashboard from './locales/pt/dashboard.json';
import ptLocations from './locales/pt/locations.json';
import ptNavigation from './locales/pt/navigation.json';
import ptOrganisation from './locales/pt/organisation.json';
import ptParties from './locales/pt/parties.json';
import ptResources from './locales/pt/resources.json';

/**
 * Languages this build ships translations for. Adding a new locale means:
 *  1. Drop sibling JSON files under `locales/<code>/<namespace>.json`.
 *  2. Add the code to this tuple.
 *  3. Add a top-level entry under `resources` below.
 *
 * The org's `languageCode` is matched against this list (case-insensitive);
 * unrecognised values fall back to English silently rather than rendering
 * missing-key placeholders.
 */
export const SUPPORTED_LANGUAGES = ['en', 'es', 'fr', 'de', 'it', 'pt'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export function isSupportedLanguage(code: string): code is SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(code);
}

/** Default + fallback. Kept as a constant so callers don't hard-code 'en'. */
export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

/**
 * `localStorage` key for the last-known good language. Namespaced per app
 * (`my:tms:lng`) because each suite app runs on its own origin and
 * therefore has its own localStorage — there's no actual cross-app sharing
 * possible via this mechanism. The JWT-driven sync (`SyncLanguageWithOrg`)
 * brings the per-app caches into agreement at login by re-applying the
 * org's `languageCode` everywhere it lands.
 *
 * Read on boot to avoid a flash of English while the org payload arrives;
 * written by `SyncLanguageWithOrg` after every org-driven language change
 * so the next boot starts in the right place.
 */
const LANGUAGE_STORAGE_KEY = 'my:tms:lng';

/** Strip a region tag (`en-IE` → `en`) before testing the supported set. */
function normaliseCode(raw: string): string {
  return raw.toLowerCase().split('-')[0] ?? '';
}

/** Read a previously-cached language. Returns null on first visit / quota errors. */
function readStoredLanguage(): SupportedLanguage | null {
  try {
    const raw = globalThis.localStorage?.getItem(LANGUAGE_STORAGE_KEY);
    if (!raw) return null;
    const code = normaliseCode(raw);
    return isSupportedLanguage(code) ? code : null;
  } catch {
    // Private-mode browsers / disabled storage / SSR — no cache, no harm.
    return null;
  }
}

/** Best-effort browser hint for first-time visitors with no cache. */
function readBrowserLanguage(): SupportedLanguage | null {
  const raw = globalThis.navigator?.language;
  if (!raw) return null;
  const code = normaliseCode(raw);
  return isSupportedLanguage(code) ? code : null;
}

/**
 * Boot-time resolution: cached preference > browser hint > default.
 * Whatever this returns is overridden by `SyncLanguageWithOrg` once the
 * org loads — but using it for the *initial* render means returning users
 * see their language from the very first paint instead of an English flash.
 *
 * Exported so the app can also pass it to Auth0 (`ui_locales`) when wiring
 * the auth provider, so Auth0's universal login renders in the same
 * language the app boots into.
 */
export function resolveBootLanguage(): SupportedLanguage {
  return readStoredLanguage() ?? readBrowserLanguage() ?? DEFAULT_LANGUAGE;
}

/**
 * Persist the active language. Called by `SyncLanguageWithOrg` after every
 * org-driven `changeLanguage` so the next boot starts on the right foot.
 * Failures (storage disabled / quota) are swallowed — caching is a
 * performance optimisation, not correctness.
 */
export function persistLanguage(code: SupportedLanguage): void {
  try {
    globalThis.localStorage?.setItem(LANGUAGE_STORAGE_KEY, code);
  } catch {
    /* see readStoredLanguage — no-op on storage failures */
  }
}

let initPromise: Promise<void> | null = null;

/**
 * Initialise the localisation layer (i18next under the hood) exactly once.
 * Subsequent calls return the same promise so concurrent boots (HMR,
 * StrictMode double-invoke, etc.) don't double-init. Call from `main.tsx`
 * `bootstrap()` *before* `createRoot().render()`.
 *
 * Returns `Promise<void>` rather than the i18n instance — callers (the
 * bootstrap, mostly) just need to await readiness. The instance itself is
 * already importable as a named export (`{ i18n }`) for the rare case that
 * needs it directly.
 */
export function initLocalisation(): Promise<void> {
  if (initPromise) return initPromise;
  // Local const + assign-then-return avoids the `Promise<void> | null`
  // narrowing issue that bites a direct `return initPromise` after the
  // assignment.
  const p = i18n
    .use(initReactI18next)
    .init({
      resources: {
        en: { common: enCommon, configuration: enConfiguration, dashboard: enDashboard, locations: enLocations, navigation: enNavigation, organisation: enOrganisation, parties: enParties, resources: enResources },
        es: { common: esCommon, configuration: esConfiguration, dashboard: esDashboard, locations: esLocations, navigation: esNavigation, organisation: esOrganisation, parties: esParties, resources: esResources },
        fr: { common: frCommon, configuration: frConfiguration, dashboard: frDashboard, locations: frLocations, navigation: frNavigation, organisation: frOrganisation, parties: frParties, resources: frResources },
        de: { common: deCommon, configuration: deConfiguration, dashboard: deDashboard, locations: deLocations, navigation: deNavigation, organisation: deOrganisation, parties: deParties, resources: deResources },
        it: { common: itCommon, configuration: itConfiguration, dashboard: itDashboard, locations: itLocations, navigation: itNavigation, organisation: itOrganisation, parties: itParties, resources: itResources },
        pt: { common: ptCommon, configuration: ptConfiguration, dashboard: ptDashboard, locations: ptLocations, navigation: ptNavigation, organisation: ptOrganisation, parties: ptParties, resources: ptResources },
      },
      // Boot language = cache > browser hint > default. The org payload
      // overrides this once it lands (see `SyncLanguageWithOrg`). The
      // fallback stays English unconditionally so any key missing from
      // a translation bundle reads through to the English copy.
      lng: resolveBootLanguage(),
      fallbackLng: DEFAULT_LANGUAGE,
      defaultNS: 'common',
      ns: ['common', 'configuration', 'dashboard', 'locations', 'navigation', 'organisation', 'parties', 'resources'],
      // React already escapes interpolated values — letting i18next
      // double-escape would mangle anything containing `<`, `&`, etc.
      interpolation: { escapeValue: false },
      // We init synchronously with bundled resources, so there's no
      // async load step to suspend on. Leaving Suspense off avoids a
      // SuspenseBoundary requirement on every translated component.
      react: { useSuspense: false },
      returnNull: false,
    })
    // `init()` resolves with the `t` function; the bootstrap doesn't
    // need it. Map to void so the public signature stays focused on
    // "i18n is ready".
    .then(() => undefined);
  initPromise = p;
  return p;
}

export { i18n };
export default i18n;
